import {
  Component, ViewChild, ElementRef, AfterViewInit, AfterContentInit, AfterViewChecked, DoCheck, Input,
  Output, EventEmitter, ContentChildren, QueryList, TemplateRef, IterableDiffers, Renderer2, forwardRef,
  ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import {isEmpty} from 'lodash';
import { DomHandler } from 'primeng/dom';
import { ObjectUtils } from 'primeng/utils';
import { PrimeTemplate } from 'primeng/api';
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import {NotificationMessageService} from "@ispm/core/components/customise-notification/notifications-message.service";
import {StringUtils} from "@ispm/shared/utils/String.utils";

export const AUTOCOMPLETE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SAutocompleteWithChips),
  multi: true
};

@Component({
  selector: 's-autoComplete',
  templateUrl: './s-auto-complete.component.html',
  styleUrls: ['./s-auto-complete.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  host: {
    '[class.p-inputwrapper-filled]': 'filled',
    '[class.p-inputwrapper-focus]': 'focus'
  },
  providers: [DomHandler, ObjectUtils, AUTOCOMPLETE_VALUE_ACCESSOR]
})
export class SAutocompleteWithChips implements AfterContentInit, AfterViewInit, DoCheck, AfterViewChecked, ControlValueAccessor {

  @Input() get suggestions(): any[] {
    return this._suggestions;
  }

  set suggestions(val: any[]) {
    this._suggestions = val;
    if (this.immutable) {
      this.handleSuggestionsChange();
    }
  }

  constructor(public el: ElementRef, differs: IterableDiffers, public renderer: Renderer2,
              private notificationMessage: NOTIFICATION_MESSAGE,
              private changeDetector: ChangeDetectorRef,
              private notificationMessageService: NotificationMessageService,) {
    this.differ = differs.find([]).create(null);
  }

  @Input() minLength: number = 1;

  @Input() delay: number = 300;

  @Input() style: any;

  @Input() styleClass: string;

  @Input() inputStyle: any;

  @Input() inputStyleClass: string;

  @Input() inputId: string;

  @Input() placeholder: string;

  @Input() readonly: boolean;

  @Input() disabled: boolean;

  @Input() maxlength: number;

  @Input() ignoreCSSClass: boolean = false;
  @Input() considerCSSClass: boolean = false;

  @Input() skipSpecialCharValidation = false;

  @Input()
  set removable(r) {
    if (r == undefined || r == null) {
      this._removable = true;
    } else {
      this._removable = r;
    }
  }

  get removable(){
    return this._removable;
  }

  private _removable:boolean = true;

  @Input() size: number;
  private _suggestions: Array<any>;

  @Input() appendTo: any;

  @Output() completeMethod: EventEmitter<any> = new EventEmitter();

  @Output() onSelect: EventEmitter<any> = new EventEmitter();
  @Output() onUnselect: EventEmitter<any> = new EventEmitter();

  @Output() onFocus: EventEmitter<any> = new EventEmitter();

  @Output() onBlurEmit: EventEmitter<any> = new EventEmitter();

  @Output() onExitEditMode: EventEmitter<any> = new EventEmitter();

  @Output() onDropdownClick: EventEmitter<any> = new EventEmitter();

  @Output() onAdd: EventEmitter<any> = new EventEmitter();

  @Output() contextMenuEmitter: EventEmitter< { event: Event, value: any}> = new EventEmitter();

  @Input() field: string;

  @Input() scrollHeight: string = '200px';

  @Input() dropdown: boolean;

  @Input() multiple: boolean;

  @Input() tabindex: number;

  @Input() classField: string = 'cssClass';

  @Input() immutable: boolean = true;

  @Input() disableToggleMenuByField: string;

  @Input() emptyMessage: string;

  @Input() autoHighlight: boolean;

  @ContentChildren(PrimeTemplate) templates: QueryList<any>;

  public itemTemplate: TemplateRef<any>;


  value: any;

  timeout: any;

  loading: boolean;

  differ: any;

  panel: any;

  input: any;

  panelVisible: boolean = false;

  documentClickListener: any;

  suggestionsUpdated: boolean;

  highlightOption: any;

  highlightOptionChanged: boolean;

  focus: boolean = false;

  dropdownFocus: boolean = false;

  filled: boolean;

  @ViewChild('in', {static: false}) inputEL: ElementRef;
  @ViewChild('multiIn', {static: false}) multiInputEL: ElementRef;
  @ViewChild('panel', {static: false}) panelEL: ElementRef;
  @ViewChild('multiContainer', {static: false}) multiContainerEL: ElementRef;
  @ViewChild('ddBtn', {static: false}) dropdownButton: ElementRef;

  public noResults: boolean;

  onModelChange: Function = () => {
  }

  onModelTouched: Function = () => {
  }

  ngDoCheck() {
    if (!this.immutable) {
      const changes = this.differ.diff(this.suggestions);
      if (changes) {
        this.handleSuggestionsChange();
      }
    }
  }

  ngAfterContentInit() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'item':
          this.itemTemplate = item.template;
          break;

        default:
          this.itemTemplate = item.template;
          break;
      }
    });
  }

  ngAfterViewInit() {
    this.input = DomHandler.findSingle(this.el.nativeElement, 'input');
    this.panel = DomHandler.findSingle(this.el.nativeElement, 'div.p-autocomplete-panel');

    this.documentClickListener = this.renderer.listen('body', 'click', () => {
      // this.hide();
    });

    if (this.appendTo) {
      if (this.appendTo === 'body') {
        document.body.appendChild(this.panel);
      }
      else {
        DomHandler.appendChild(this.panel, this.appendTo);
      }
    }
  }

  ngAfterViewChecked() {
    if (this.suggestionsUpdated) {
      this.align();
      this.suggestionsUpdated = false;
    }

    if (this.highlightOptionChanged) {
      const listItem = DomHandler.findSingle(this.panel, 'li.p-state-highlight');
      if (listItem) {
        DomHandler.scrollInView(this.panel, listItem);
      }
      this.highlightOptionChanged = false;
    }
  }

  handleSuggestionsChange() {
    if (this.suggestions != null) { // async pipe support
      if (this.panelEL && this.panelEL.nativeElement && this.loading) {
        this.highlightOption = null;
        if (this.suggestions && this.suggestions.length) {
          this.noResults = false;
          this.show();
          this.suggestionsUpdated = true;

          if (this.autoHighlight) {
            this.highlightOption = this.suggestions[0];
          }
        }
        else {
          this.noResults = true;

          if (this.emptyMessage) {
            this.show();
            this.suggestionsUpdated = true;
          }
          else {
            this.hide();
          }
        }
      }

      this.loading = false;
    }
  }


  writeValue(value: any): void {
    this.value = value;
    this.filled = this.value && this.value != '';
    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  onInput(event: any) {
    const value = event.target.value;
    if (!this.multiple) {
      this.value = value;
      this.onModelChange(value);
    }

    if (value.length === 0) {
      this.hide();
    }

    if (value.length >= this.minLength) {
      // Cancel the search request if user types within the timeout
      if (this.timeout) {
        clearTimeout(this.timeout);
      }

      this.timeout = setTimeout(() => {
        this.search(event, value);
      }, this.delay);
    }
    else {
      this.suggestions = null;
    }
    this.updateFilledState();
  }

  search(event: any, query: string) {
    // allow empty string but not undefined or null
    if (query === undefined || query === null) {
      return;
    }

    this.suggestions && (this.loading = true);

    this.completeMethod.emit({
      originalEvent: event,
      query: query
    });
  }

  selectItem(option: any) {
    this.loading = false;
    if (!option || option == '') {
      return;
    }

    if ((typeof option === 'string') && StringUtils.isInvalidCharsInFileName(option) && !this.skipSpecialCharValidation) {
      this.multiInputEL.nativeElement.value = '';
      this.notificationMessageService.error(`${this.notificationMessage.TAG_CONTAINS_HTML}`);
      return;
    }

    if (this.multiple) {
      this.input.value = '';
      this.value = this.value || [];
      if (!this.isSelected(option)) {
        let val = option;
        if (this.field && typeof val == 'string') {
          val = { };
          val[this.field] = option;
        }
        if (val && typeof val === 'object') {
          if(!val.hasOwnProperty('code') && val.hasOwnProperty('display'))
          {
            val.code = val.display;
            val.system = 'MANUAL';
          }
          if (!this.ignoreCSSClass) {
            val[this.classField] = 'tagLabelDefault';
          }
          /*---- Below logic condition seems redundant but required as above ignoreCSSclass is making it necessary
           as without ignoreclass class names from form is not considered with it default class is not considered
           clearly some issue in th above fix*/

          if(this.considerCSSClass && isEmpty(val[this.classField])){
            val[this.classField] = 'tagLabelDefault';
          }

           // adding the field specified in disableToggleMenuByField to dropdown selected object.
           if(!isEmpty(this.disableToggleMenuByField) ){
            val[this.disableToggleMenuByField] = option[this.disableToggleMenuByField];
          }
        }


        this.value = [...this.value];
        this.value.push(val);
        this.hide();
        this.onModelChange(this.value);
      }
    }
    else {
      this.input.value = this.field ? this.resolveFieldData(option) : option;
      this.value = option;
      this.hide();
      this.onModelChange(this.value);
    }

    if (typeof option === 'object') {
      this.onSelect.emit(option);
    } else {
      this.onSelect.emit(option);
      this.onAdd.emit({
        originalEvent: event,
        value: option
      });
    }


    this.input.focus();
  }

  show() {
    if (!this.panelVisible && (this.focus || this.dropdownFocus)) {
      this.panelVisible = true;
      this.panel.style.zIndex = 1017;
      DomHandler.fadeIn(this.panel, 200);
    }
  }

  align() {
    if (this.appendTo) {
      DomHandler.absolutePosition(this.panel, (this.multiple ? this.multiInputEL.nativeElement : this.input));
    }
    else {
      DomHandler.relativePosition(this.panel, (this.multiple ? this.multiInputEL.nativeElement : this.input));
    }
  }

  hide() {
    this.loading = false;
    this.panelVisible = false;
  }

  handleDropdownClick(event: any) {
    const nativeElement = this.multiple ? this.multiInputEL.nativeElement : this.inputEL.nativeElement;
    const queryValue = nativeElement.value;
    this.search(event, queryValue);

    this.onDropdownClick.emit({
      originalEvent: event,
      query: queryValue
    });
  }

  removeItem(item: any) {
    const itemIndex = DomHandler.index(item);
    this.value = [...this.value];
    const removedValue = this.value[itemIndex];
    this.onUnselect.emit(removedValue);
    this.value = this.value.filter((_,index) => index !== itemIndex);
    this.onModelChange(this.value);
  }

  resolveFieldData(data: any): any {
    if (data && this.field) {
      if (this.field.indexOf('.') == -1) {
        return data[this.field];
      }
      else {
        const fields: string[] = this.field.split('.');
        let value = data;
        for (let i = 0, len = fields.length; i < len; ++i) {
          value = value[fields[i]];
        }
        return value;
      }
    }
    else {
      return null;
    }
  }

  onKeydown(event: any) {
    let noHighlightOption = true;
    if (this.panelVisible && this.suggestions && this.suggestions.length > 0) {
      const highlightItemIndex = this.findOptionIndex(this.highlightOption);
      noHighlightOption = highlightItemIndex == -1;
      switch (event && event.which) {
        // down
        case 40:
          if (this.suggestions.length > 0) {
            const nextItemIndex = highlightItemIndex + 1;
            if (nextItemIndex != (this.suggestions.length)) {
              this.highlightOption = this.suggestions[nextItemIndex];
              this.highlightOptionChanged = true;
              noHighlightOption = false;
            }
          }
          else {
            this.highlightOption = this.suggestions[0];
            noHighlightOption = false;

          }
          event.preventDefault();
          break;

        // up
        case 38:
          if (highlightItemIndex > 0) {
            const prevItemIndex = highlightItemIndex - 1;
            this.highlightOption = this.suggestions[prevItemIndex];
            this.highlightOptionChanged = true;
          } else {
            noHighlightOption = true;
          }

          event.preventDefault();
          break;

        // enter
        case 13:
          if (this.highlightOption) {
            this.selectItem(this.highlightOption);
            this.hide();
          }
          event.preventDefault();
          break;

        // escape
        case 27:
          this.hide();
          event.preventDefault();
          break;


        // tab
        case 9:
        // blur
        case 0:
          if (this.highlightOption) {
            this.selectItem(this.highlightOption);
          }
          this.hide();
          break;
      }
    }

    if (noHighlightOption) {
      if (event && event.which === 40 && this.suggestions) {
        this.search(event, event.target.value);
      } else if (event && event.which === 13) {
        this.selectItem(event.target.value);
        event.preventDefault();
      } else if (event && (event.which === 9 ||  event.which === 0)) {
        this.selectItem(event.target.value);
      }
    }

    if (this.multiple) {
      switch (event && event.which) {
        // backspace
        case 8:
          if (this.value && this.value.length && !this.input.value) {
            this.value = [...this.value];
            const itemIndex = this.value.length -1;
            const removedValue = this.value.pop();
            this.onUnselect.emit(removedValue);
            this.value = this.value.filter((_,index)=> index !== itemIndex)
            this.onModelChange(this.value);
          }
          break;
      }
    }
  }

  onFocusMethod($event) {
    this.focus = true;
    this.onFocus.emit($event);
  }

  onBlur($event) {
    this.focus = false;
    this.onModelTouched();
    $event.relatedTarget ? this.onBlurEmit.emit($event) : this.onExitEditMode.emit($event);
    this.onModelChange(this.value)
    this.onKeydown($event);
    this.hide();
  }

  onDropdownFocus() {
    this.dropdownFocus = true;
    const nativeElement = this.multiple ? this.multiInputEL.nativeElement : this.inputEL.nativeElement;
    nativeElement.focus();
  }

  onDropdownBlur() {
    this.dropdownFocus = false;
  }

  isSelected(val: any): boolean {
    let selected: boolean = false;
    if (this.value && this.value.length) {
      val = this.field && typeof val === 'object' ? val[this.field] : val;
      selected = !!this.value.find(value => {
        value = this.field ? value[this.field] : value;
        return ObjectUtils.equals(value.toLowerCase(), val.toLowerCase());
      });
    }
    return selected;
  }

  findOptionIndex(option: any): number {
    let index: number = -1;
    if (this.suggestions) {
      for (let i = 0; i < this.suggestions.length; i++) {
        if (ObjectUtils.equals(option, this.suggestions[i])) {
          index = i;
          break;
        }
      }
    }

    return index;
  }

  updateFilledState() {
    this.filled = this.input && this.input.value != '';
  }

  emitContextMenu($event, value) {
    this.contextMenuEmitter.emit({ event: $event, value});
  }

  trackByFn(index, item) {
    return typeof item === 'object' ? index : item;
  }

  ngOnDestroy() {
    if (this.documentClickListener) {
      this.documentClickListener();
    }

    if (this.appendTo) {
      this.el.nativeElement.appendChild(this.panel);
    }
  }

  arrayCheck(value) {
    return Array.isArray(value);
  }
}
